import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Alert, CircularProgress, Snackbar,
  Paper, Grid, Card, CardContent
} from '@mui/material';
import {
  AdminPanelSettings, MedicalServices, Inventory2,
  Hotel, People, Category, Restaurant
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import TreatmentsTab from './TreatmentsTab';
import PackagesTab from './PackagesTab';
import RoomsTab from './RoomsTab';
import UsersTab from './UsersTab';
import DietItemsTab from './DietItemsTab';

const TABS = [
  { label: 'Treatments', icon: <MedicalServices /> },
  { label: 'Packages', icon: <Inventory2 /> },
  { label: 'Rooms & Categories', icon: <Hotel /> },
  { label: 'Diet Items', icon: <Restaurant /> },
  { label: 'Users', icon: <People /> }
];

export default function AdminDashboard({ user }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [treatments, setTreatments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dietItems, setDietItems] = useState([]);
  const [users, setUsers] = useState([]);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes, rRes, cRes, dRes, uRes] = await Promise.all([
        adminAPI.getTreatments(),
        adminAPI.getPackages(),
        adminAPI.getRooms(),
        adminAPI.getRoomCategories(),
        adminAPI.getDietItems(),
        adminAPI.getUsers()
      ]);
      setTreatments(tRes.data.data || []);
      setPackages(pRes.data.data || []);
      setRooms(rRes.data.data || []);
      setCategories(cRes.data.data || []);
      setDietItems(dRes.data.data || []);
      setUsers(uRes.data.data || []);
    } catch (e) {
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const stats = [
    { label: 'Treatments', value: treatments.length, icon: <MedicalServices color="primary" />, color: '#e3f2fd' },
    { label: 'Packages', value: packages.length, icon: <Inventory2 color="secondary" />, color: '#fce4ec' },
    { label: 'Rooms', value: rooms.length, icon: <Hotel color="warning" />, color: '#fff3e0' },
    { label: 'Categories', value: categories.length, icon: <Category color="success" />, color: '#e8f5e9' },
    { label: 'Diet Items', value: dietItems.length, icon: <Restaurant sx={{ color: 'brown' }} />, color: '#efebe9' },
    { label: 'Users', value: users.length, icon: <People color="error" />, color: '#fbe9e7' }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AdminPanelSettings color="primary" sx={{ fontSize: 36 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Admin Dashboard</Typography>
          <Typography variant="caption" color="text.secondary">
            Manage system entities — treatments, packages, rooms and users
          </Typography>
        </Box>
      </Box>

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map(s => (
          <Grid item xs={6} sm={4} md key={s.label}>
            <Card variant="outlined" sx={{ bgcolor: s.color }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {s.icon}
                <Box>
                  <Typography variant="h5" fontWeight="bold" lineHeight={1}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {TABS.map((t, i) => (
            <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {tab === 0 && (
            <TreatmentsTab
              rows={treatments}
              onRefresh={loadAll}
              showSnackbar={showSnackbar}
            />
          )}
          {tab === 1 && (
            <PackagesTab
              rows={packages}
              treatments={treatments}
              onRefresh={loadAll}
              showSnackbar={showSnackbar}
            />
          )}
          {tab === 2 && (
            <RoomsTab
              rooms={rooms}
              categories={categories}
              onRefresh={loadAll}
              showSnackbar={showSnackbar}
            />
          )}
          {tab === 3 && (
            <DietItemsTab
              rows={dietItems}
              onRefresh={loadAll}
              showSnackbar={showSnackbar}
            />
          )}
          {tab === 4 && (
            <UsersTab
              rows={users}
              currentUserId={user?.id}
              onRefresh={loadAll}
              showSnackbar={showSnackbar}
            />
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
